import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const mq = (q) => typeof window !== "undefined" && window.matchMedia && window.matchMedia(q).matches;

export const useMedia = (query) => {
  const [match, setMatch] = useState(() => mq(query));
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return match;
};

export const useReducedMotion = () => useMedia("(prefers-reduced-motion: reduce)");
export const useFinePointer = () => useMedia("(hover: hover) and (pointer: fine)");

export function RevealManager() {
  const reduced = useReducedMotion();
  useEffect(() => {
    const html = document.documentElement;
    if (reduced || !("IntersectionObserver" in window)) {
      html.classList.remove("motion-ok");
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        }),
      { rootMargin: "0px 0px -6% 0px", threshold: 0.06 }
    );
    let raf = 0;
    const scan = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        document.querySelectorAll("[data-reveal]:not(.is-in)").forEach((el) => io.observe(el))
      );
    };
    const reveal = () => document.querySelectorAll("[data-reveal]").forEach((el) => el.classList.add("is-in"));
    scan();
    html.classList.add("motion-ok");
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("beforeprint", reveal);
    return () => {
      io.disconnect();
      mo.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener("beforeprint", reveal);
    };
  }, [reduced]);
  return null;
}

export function SmoothScroll() {
  const reduced = useReducedMotion();
  const fine = useFinePointer();
  useEffect(() => {
    if (reduced || !fine) return;
    let cleanup = () => {};
    let cancelled = false;
    Promise.all([import("lenis"), import("gsap"), import("gsap/ScrollTrigger")]).then(([{ default: Lenis }, { gsap }, { ScrollTrigger }]) => {
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      window.__ST = ScrollTrigger;
      const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
      window.__lenis = lenis;
      lenis.on("scroll", ScrollTrigger.update);
      const tick = (t) => lenis.raf(t * 1000);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
      cleanup = () => {
        gsap.ticker.remove(tick);
        lenis.destroy();
        window.__lenis = null;
      };
    });
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [reduced, fine]);
  return null;
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    requestAnimationFrame(() => window.__ST?.refresh());
  }, [pathname]);
  return null;
}
